import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { toast } from "react-toastify";
import { Icons } from "../../constants/icon-library";
import {
  upsertParticipant,
  getParticipantById,
} from "../../features/participant/participantSlice";
import {
  CREATE_PARTICIPANT_INITIAL_VALUES,
  createParticipantValidationSchema,
} from "../../constants/participant.constant";
import MultiSelect from "../../components/common/MultiSelect";
import { fetchSettingDetailsByUserId } from "../../features/settings/settingsSlice";

function CreateParticipant() {
  const { participant, loading } = useSelector((state) => state.participants);
  const { userSettings } = useSelector((state) => state.setting);

  const { uuid: participantId } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (participantId) {
      dispatch(getParticipantById(participantId));
    }
  }, [participantId, dispatch]);

  useEffect(() => {
    dispatch(fetchSettingDetailsByUserId());
  }, [dispatch]);

  const uniquePlatforms = [
    ...new Map(
      (userSettings || []).map((item) => [
        item?.assistantId?.platform?.uuid,
        {
          label: item?.assistantId?.platform?.name,
          value: item?.assistantId?.platform?.type,
        },
      ]),
    ).values(),
  ];

  const initialValues =
    participantId && participant
      ? {
          name: participant?.name || "",
          username: participant?.username || "",
          platforms: participant?.platforms || [],
        }
      : CREATE_PARTICIPANT_INITIAL_VALUES;

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const result = await dispatch(
        upsertParticipant({
          ...(participantId && { uuid: participantId }),
          name: values.name.trim(),
          username: values.username.trim(),
          platforms: values.platforms,
        }),
      );

      if (result?.payload?.uuid) {
        toast.success(
          participantId
            ? "Participant updated successfully"
            : "Participant created successfully",
        );

        navigate(-1);
      } else {
        toast.error(result?.payload?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to save participant");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="content">
      <div className="row pt-3">
        <div className="card border p-0">
          <div className="card-header p-4 border-bottom bg-body d-flex align-items-center">
            <i className={`wb ${Icons("add")} fs-8 me-2`} />
            <h3 className="mb-0">
              {participantId ? "Edit Participant" : "Add Participant"}
            </h3>
          </div>

          <div className="card-body">
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={createParticipantValidationSchema}
              onSubmit={handleSubmit}
            >
              {({
                isSubmitting,
                isValid,
                errors,
                touched,
                values,
                setFieldValue,
              }) => (
                <Form>
                  <div className="mb-4">
                    <label className="fw-bold mb-2">Name</label>

                    <Field
                      type="text"
                      name="name"
                      className={`form-control ${
                        touched.name && errors.name ? "is-invalid" : ""
                      }`}
                      placeholder="Enter participant name"
                    />

                    <div className="text-danger small">
                      <ErrorMessage name="name" />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="fw-bold mb-2">Email/Username</label>

                    <Field
                      type="text"
                      name="username"
                      className={`form-control ${
                        touched.username && errors.username ? "is-invalid" : ""
                      }`}
                      placeholder="Enter participant username"
                    />

                    <div className="text-danger small">
                      <ErrorMessage name="username" />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="fw-bold mb-2">Platform</label>

                    <MultiSelect
                      options={uniquePlatforms}
                      value={values.platforms || []}
                      onChange={(value) => setFieldValue("platforms", value)}
                      placeholder="Select platform..."
                    />

                    <div className="text-danger small">
                      <ErrorMessage name="platforms" />
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => navigate(-1)}
                      disabled={loading}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      disabled={loading || isSubmitting || !isValid}
                    >
                      {loading || isSubmitting
                        ? "Saving..."
                        : "Save Participant"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateParticipant;
